<template>
  <LayoutContainer
    :id="data.cssId"
    class="dbc-gc-form--contact"
    :aos="{
      fadeDirection: 'fade-right',
      delay: 300,
    }"
  >
    <div ref="container" class="scroll-m-20">
      <UtilityHeadline
        v-if="data.headlineH2"
        :class="`dbc-text-color-${data.headlineH2Color}`"
        :text="data.headlineH2"
        :level="2"
      />

      <div v-if="!isSent && !hasError">
        <UtilityText
          v-if="data.text"
          :class="`dbc-text-color-${data.textColor}`"
          :text="data.text"
        />

        <FormsContactFlinkster
          v-if="client === 'flinkster'"
          @onSubmitContactZnuny="onSubmitContactZnuny"
        />

        <FormsContactB2B
          v-else-if="data.form === 'b2b'"
          :isLoading="isLoading"
          @onSubmitContactB2B="onSubmitContact"
          :errorCode="errorCode"
        />

        <FormsContactB2C
          v-else-if="data.form === 'b2c'"
          :isLoading="isLoading"
          @onSubmitContactZnuny="onSubmitContactZnuny"
          :errorCode="errorCode"
        />

        <FormsContactB2G
          v-else-if="data.form === 'b2g'"
          :isLoading="isLoading"
          @onSubmitContactB2G="onSubmitContact"
          :errorCode="errorCode"
        />
      </div>

      <div ref="isSentMessageTemplateRef" tabindex="-1">
        <div v-if="isSent">
          <strong>
            <UtilityText
              class="dbc-form-prompt"
              :text="$t(`dbc-contact-${data.form}-text-thanks-for-the-request`)"
            />
          </strong>
          <UtilityText :text="teamGreeting" />
        </div>
      </div>

      <div ref="hasErrorMessageTemplateRef" tabindex="-1">
        <div v-if="hasError">
          <UtilityText
            class="dbc-u-error-txt"
            :text="$t(`dbc-error-server-issues`)"
          />
          <UtilityButton
            class="dbc-cancel-contract-info-box-button"
            :button="{
            type: 'primary',
            link: '',
            text: $t('dbc-cancel-contract-box-error-button')!,
          }"
            @click.prevent="onClickTryAgain"
          />
        </div>
      </div>
    </div>
  </LayoutContainer>
</template>

<script setup lang="ts">
const props = defineProps<{
  content: ContentContactForm
}>()

const data = normalizeContentContactForm(props.content)

const { t } = useI18n()

const route = useRoute()

const runtimeConfig = useRuntimeConfig()

const client = runtimeConfig.public.client as string

const teamGreeting = ref(t(`dbc-contact-${data.form}-text-team-greeting`))

const clientMapping = runtimeConfig.public.clientMapping as ClientMapping
teamGreeting.value = teamGreeting.value!.replace(
  '[placeholder]',
  clientMapping[client]!
)

const isLoading = ref(false)
const isSent = ref(false)
const isSentMessageTemplateRef = ref<HTMLElement | null>(null)

const hasError = ref(false)
const hasErrorMessageTemplateRef = ref<HTMLElement | null>(null)

const container = ref<HTMLInputElement | null>(null)

watch([hasError, isSent], async () => {
  if (hasError.value === true || isSent.value === true) {
    const { $aosRefresh } = useNuxtApp()
    container.value?.scrollIntoView()
    $aosRefresh()
  }
})

function onClickTryAgain() {
  isLoading.value = false
  hasError.value = false
}

const errorCode = ref('')

async function onSubmitContact(formData: ContactFormData) {
  isLoading.value = true

  if (!formData.topic) formData.topic = '<leer>'
  if (!formData.message) formData.message = '<leer>'

  const response = await fetchContactCreate(formData)
  isLoading.value = false

  if (response._data?.data?.errorCode) {
    errorCode.value = response._data.data.errorCode
    return
  } else if (response.status !== 200) {
    hasError.value = true
    hasErrorMessageTemplateRef.value?.focus()
  }
  if (response.status === 200) {
    isSent.value = true
    isSentMessageTemplateRef.value?.focus()
    if (formData.type === 'b2b') {
      const utmSource = route.query.utm_source || ''
      trackEvent(`b2b contact ${utmSource}`, new Date().toLocaleString('de-DE'))
    }
  }
}

async function onSubmitContactZnuny(formData: ContactFormData) {
  isLoading.value = true
  const response = ref()
  window.OMQContact.submit()

  switch (client) {
    case 'flinkster':
      response.value = await fetchContactCreateFlinkster(formData)
      break
    default:
      response.value = await fetchContactCreateB2C(formData)
  }

  isLoading.value = false

  if (response.value.status !== 200) {
    hasError.value = true
    hasErrorMessageTemplateRef.value?.focus()
  }

  if (response.value.status === 200) {
    isSent.value = true
    isSentMessageTemplateRef.value?.focus()
  }
}
</script>
